/**
 * Consent gate for analytics and marketing integrations, backed by Shopify's Customer Privacy API.
 *
 * Resolution per purpose ('analytics' | 'marketing' | 'preferences'):
 *   - 'yes' / 'no' from currentVisitorConsent() is used as-is.
 *   - Unanswered while the banner is pending (shouldShowBanner()) => not granted; integrations wait.
 *   - Unanswered where no banner is shown => Shopify's regional default (*Allowed()).
 *   - Marketing additionally requires saleOfDataAllowed() (CCPA "Do Not Sell or Share").
 *   - If the API cannot load, nothing is granted (fail closed).
 *
 * If consent for a purpose is withdrawn after one of its integrations started, the page reloads so
 * the third-party script is no longer present.
 *
 * Usage:
 *   BSSConsent.whenGranted('marketing', () => { ...load pixel... });
 *   BSSConsent.onChange(state => { ...state.analytics / state.marketing / state.preferences... });
 *   BSSConsent.loadScript(src).then(...);
 */
(function () {
  const PURPOSES = ['analytics', 'marketing', 'preferences'];
  const DENIED = Object.freeze({
    analytics: false,
    marketing: false,
    preferences: false
  });

  const pending = [];
  const listeners = [];
  const startedPurposes = new Set();
  let api = null;
  let ready = false;
  let notified = false;
  let state = DENIED;

  /** Calls a Customer Privacy API method, returning `fallback` if it is missing or throws. */
  const call = (fn, fallback) => {
    try {
      return typeof fn === 'function' ? fn.call(api) : fallback;
    } catch (e) {
      return fallback;
    }
  };

  /** Resolves the current granted/denied state for each purpose from the Customer Privacy API. */
  function readState() {
    if (!api) return DENIED;

    const consent = call(api.currentVisitorConsent, null) || {};
    const awaitingBanner = call(api.shouldShowBanner, false) === true;
    const resolve = (value, regionalDefault) => {
      if (value === 'yes') return true;
      if (value === 'no') return false;
      return awaitingBanner ? false : call(regionalDefault, false) === true;
    };

    return {
      analytics: resolve(consent.analytics, api.analyticsProcessingAllowed),
      marketing:
        resolve(consent.marketing, api.marketingAllowed) &&
        call(api.saleOfDataAllowed, true) !== false,
      preferences: resolve(
        consent.preferences,
        api.preferencesProcessingAllowed
      )
    };
  }

  /** Accepts a single purpose or a list of purposes. */
  const normalize = purposes =>
    Array.isArray(purposes) ? purposes : [purposes];

  /** True when every listed purpose is currently granted. */
  const isGranted = purposes =>
    normalize(purposes).every(p => state[p] === true);

  /** Starts an integration and remembers its purposes so a later withdrawal can reload the page. */
  function run(entry) {
    normalize(entry.purposes).forEach(p => startedPurposes.add(p));
    try {
      entry.callback();
    } catch (e) {
      console.error('[BSSConsent] integration failed:', e);
    }
  }

  /** Starts every queued integration whose purposes are now granted, in registration order. */
  function flush() {
    const granted = pending.filter(entry => isGranted(entry.purposes));
    granted.forEach(entry => pending.splice(pending.indexOf(entry), 1));
    granted.forEach(run);
  }

  /**
   * Re-reads consent (on API ready and on every visitorConsentCollected event), reloads if a started
   * purpose was withdrawn, notifies onChange listeners of changes, then starts newly allowed integrations.
   */
  function update() {
    if (!ready) return;
    const previous = state;
    state = Object.freeze(readState());

    const revoked = PURPOSES.some(
      p => previous[p] && !state[p] && startedPurposes.has(p)
    );
    if (revoked) {
      window.location.reload();
      return;
    }

    const changed = PURPOSES.some(p => previous[p] !== state[p]);
    if (changed || !notified) {
      notified = true;
      listeners.forEach(listener => {
        try {
          listener(state);
        } catch (e) {
          console.error('[BSSConsent] listener failed:', e);
        }
      });
    }
    flush();
  }

  /** Records the loaded API (or null when unavailable) and applies the initial consent state. */
  function onReady(customerPrivacy) {
    api = customerPrivacy || null;
    ready = true;
    update();
  }

  /** Listens for banner answers and loads Shopify's consent-tracking-api feature. */
  function init() {
    const shopify = window.Shopify;
    document.addEventListener('visitorConsentCollected', update);

    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      onReady(null);
      return;
    }
    shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      error => {
        if (error)
          console.error(
            '[BSSConsent] consent-tracking-api failed to load:',
            error
          );
        onReady(error ? null : shopify.customerPrivacy);
      }
    );
  }

  window.BSSConsent = {
    /** Runs `callback` once every listed purpose is granted (immediately if already granted). */
    whenGranted(purposes, callback) {
      const entry = { purposes, callback };
      if (ready && isGranted(purposes)) run(entry);
      else pending.push(entry);
    },
    /** Calls `listener(state)` once consent is known and again whenever it changes. */
    onChange(listener) {
      listeners.push(listener);
      if (ready) listener(state);
    },
    /** True when every listed purpose is currently granted. */
    isGranted,
    /** Returns the current { analytics, marketing, preferences } state. */
    getState: () => state,
    /** Injects an async script tag; resolves on load and rejects on error. */
    loadScript(src, attributes = {}) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        Object.entries(attributes).forEach(([k, v]) =>
          script.setAttribute(k, v)
        );
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
  };

  init();
})();